import { ReactNode, useEffect } from "react";
import { ValidatedInputProps } from "./Common";
import { useForceUpdate } from "../../Hooks";
import commonStyles from "./Common.module.scss";
import { classNames } from "../../Utilities";
import styles from "./Tags.module.scss";
import { Autocomplete } from "./Autocomplete";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface TagsProps<T> {
    id: string;
    label: string;
    items: T[];
    itemId: (item: T) => string;
    itemDescription: (item: T) => string;
    value?: string[];
    onChange?: (value: string[]) => void;
}

export const Tags = <T,>(props: TagsProps<T> & ValidatedInputProps): ReactNode => {
    const forceUpdate = useForceUpdate();

    useEffect(() => {
        props.validator?.register({
            key: props.id,
            validations: props.validations,
            value: props.value,
            forceUpdate: forceUpdate,
            validationMessages: props.customMessages
        });

        return () => {
            props.validator?.unregister(props.id);
        };
    }, []);

    useEffect(() => {
        if (props.validator) {
            props.validator.setValue(props.id, props.value ?? null);
            forceUpdate();
        }
    }, [props.value]);

    const handleItemSelected = (item: T) => {
        props.onChange?.([...(props.value ?? []), props.itemId(item)]);
    };

    const isErrorState = (): boolean => {
        return !!props.validator && props.validator.submitAttempted() && props.validator.getErrors(props.id).length > 0;
    };

    return (
        <div className={commonStyles.container}>
            {!!props.label && (
                <div className={classNames([commonStyles.label, isErrorState() && commonStyles.error])}>
                    <label htmlFor={props.id}>{props.label}</label>
                </div>
            )}

            {!!props.value?.length && (
                <div className={styles.tags}>
                    {props.value.map((v) => {
                        const item = props.items.find(i => props.itemId(i) === v);
                        if (item == null) return null;
                        return (
                            <div key={props.itemId(item)} className={styles.tag}>
                                <span>{props.itemDescription(item)}</span>
                                {!!props.onChange && (
                                    <button
                                        type="button"
                                        className="asLink"
                                        onClick={() => props.onChange?.(props.value?.filter(f => f !== v) ?? [])}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!!props.onChange && (
                <Autocomplete
                    id={`${props.id}-autocomplete`}
                    onAutocomplete={s => Promise.resolve(props.items.filter(i => props.itemDescription(i).toLowerCase().includes(s.toLowerCase())).slice(0, 5))}
                    onAutocompleteSelected={handleItemSelected}
                    autocompleteOptionKey={props.itemId}
                    autocompleteDisplayOption={props.itemDescription}
                    autoClear={true}
                />
            )}
        </div>
    );
};